import { configModule } from '@/store/config';
import { alphabeticalSort, computeFieldI18nItems, computeFieldItems } from '@/common/utilsFilterSchema';
import {
  configButtonGroup,
  configCarModel,
  configInput,
  configLicensePlateInput,
  configLicensePlateInputSmall,
  configMultipleInputSmall,
  configSelect,
  configSelectAsTags,
  configSelectAsTagsSmall,
  configSmallCarModel,
  configureAge,
  configureAngle,
  configureConfidence,
  configureRange,
  configureSmallAge,
  configureSmallAngle,
  SimpleFilterOptions
} from '@/components/common/filter/filters/types';
import { isDefined } from '@/definitions/services/helpers';
import { IFormLayoutItem } from '@/uikit/forms/NForm.vue';
import { filterRecursive } from '@/uikit/helpers';
import { addRowClasses, ConfidenceOptions, decodeFormItemName, decodeScore, entcodeScore, getListFieldWithConfidence, Positions } from './utils';
import { featuresModule } from '@/store/features';
import { ObjectsTypesMap } from '@/store/application/data.assets';

export class AttributeFilterSchemaModule {  

  get carFeatures() {
    return configModule.config.objects?.cars?.features;
  }

  get attributePresentInConfigMap(): Record<string, string> {
    return {
      liveness_gte: 'liveness',

      license_plate_number_contains: 'license_plate_number',
      license_plate_number_color_confidence_gte: 'license_plate_number_color',
      category_confidence_gte: 'category_type',
      orientation_confidence_gte: 'orientation',
      weight_type_confidence_gte: 'weight_type',

      gender_score_gte: 'gender',
      age_group_score_gte: 'age_group',
      helmet_type_score_gte: 'helmet_type',
      vest_type_score_gte: 'vest_type',
      handface_taking_pictures_score_gte: 'handface_taking_pictures',

      eyes_attrs_score_gte: 'eyes_attrs',
      fall_score_gte: 'fall',

      handface_smoking_score_gte: 'handface_smoking',
      handface_phone_use_score_gte: 'handface_phone_use',
      handface_phone_call_score_gte: 'handface_phone_call',
      bag_back_score_gte: 'bag_back',
      bag_hand_score_gte: 'bag_hand'
    };
  }

  getFaceAttributes(
    options: SimpleFilterOptions,
    isWebhook = false,
    hasLiveness = true,
    isCluster = false
  ): (IFormLayoutItem | IFormLayoutItem[] | undefined)[] {
    return featuresModule.getFeatureFilters(options, ObjectsTypesMap.Faces, isWebhook, hasLiveness, isCluster);
  }

  getBodyAttributes(options: SimpleFilterOptions, hasBags = true, isWebhook = false, isCluster = false): (IFormLayoutItem | IFormLayoutItem[] | undefined)[] {
    return featuresModule.getFeatureFilters(options, ObjectsTypesMap.Bodies, isWebhook, false, isCluster);
  }

  getCarAttributes(options: SimpleFilterOptions, isWebhook = false): (IFormLayoutItem | IFormLayoutItem[] | undefined)[] {
    return featuresModule.getFeatureFilters(options, ObjectsTypesMap.Cars, isWebhook, false, false);
  }

  getCarMake(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'make',
      i18n_label: 'features.cars_make',
      i18n_placeholder: 'common.choose_make',
      multiple: true,
      items: computeFieldItems(this.carFeatures.make.classes)
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  getCarModel(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      path: 'model',
      name: 'model',
      i18n_label: 'features.cars_model',
      i18n_placeholder: 'common.choose_model',
      multiple: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSmallCarModel(resultOptions) : configCarModel(resultOptions);
  }

  getCarLicensePlateCountry(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'license_plate_country',
      i18n_label: 'features.cars_license_plate_country',
      readonly: false,
      anyItem: true,
      multiple: true,
      items: alphabeticalSort(computeFieldI18nItems(this.carFeatures.license_plate_country.classes, 'features.cars_license_plate_country')),
      i18n_placeholder: 'common.select_country'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCarLicensePlateNumberColor(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'license_plate_number_color',
      i18n_label: 'features.cars_license_plate_number_color',
      anyItem: true,
      multiple: true,
      readonly: true,
      items: computeFieldI18nItems(this.carFeatures.license_plate_number_color.classes, 'features.cars_license_plate_number_color', true)
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getConfidence(options: SimpleFilterOptions) {
    return configureConfidence(options);
  }

  getClassFeatureFilter(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: options.path,
      i18n_label: options.i18n_label,
      anyItem: true,
      multiple: true,
      readonly: true,
      items: options.items
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getClassFeatureFilterWithConfidence(options: SimpleFilterOptions): IFormLayoutItem[] {
    const confidenceOptions: ConfidenceOptions = {
      primaryFieldName: options.path!,
      configPrimaryField: this.getClassFeatureFilter.bind(this),
      confidenceFieldName: options.path + '_score_gte'
    };
    return getListFieldWithConfidence(options, confidenceOptions);
  }

  getInputFeatureContains(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {};
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getAngleFeatureFilter(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      name: options.name,
      i18n_label: options.i18n_label
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small
      ? configureSmallAngle(resultOptions, options.name + '_angle_gte', options.name + '_angle_lte')
      : configureAngle(resultOptions, options.name + '_angle_gte', options.name + '_angle_lte');
  }

  getAgeFeatureFilter(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: options.i18n_label
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configureSmallAge(resultOptions) : configureAge(resultOptions);
  }
}

export const attributeFilterSchemaModule = new AttributeFilterSchemaModule();
